/**
 * 크레딧 충전 페이지 — 패키지 선택 + 토스페이먼츠 v2 결제 위젯
 *
 *  흐름:
 *    1. preOrder 호출 → orderId/customerKey/clientKey 발급
 *    2. 토스 v2 SDK 로드 → widgets({ customerKey })
 *    3. setAmount + renderPaymentMethods + renderAgreement
 *    4. [결제하기] → widgets.requestPayment() → 토스 결제창
 *    5. successUrl(/business/layout/user-credit-wallet-charge-success) 로 리다이렉트
 *
 *  ⚠ 기존 customer/layout/payment 와 코드 패턴은 같지만 완전 분리된 라우트.
 *    토스 키/SDK 만 공유. (BE 도 별도 API)
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useRef, useCallback } from "react";
import { useToastStore } from "&/toast";
import { LayoutHeader } from "@/Header";
import MessageModal from "@/MessageModal";
import { useCreditPackages, useCompanyCreditWallet, type CreditPackage } from "~/services/business/credit";
import { walletChargeApi, type BuyCreditPreOrderResponse } from "~/services/business/walletCharge";
import { CREDIT } from "%/credit";

export const Route = createFileRoute("/business/layout/user-credit-wallet-charge")({
  component: WalletCharge,
});

function WalletCharge() {
  const { add } = useToastStore();
  const navigate = useNavigate();

  const { data: packages = [], isLoading: pkgLoading } = useCreditPackages();
  const { data: wallet } = useCompanyCreditWallet(); // 상단 보유 크레딧 표시용
  const [selectedPkg, setSelectedPkg] = useState<CreditPackage | null>(null);
  const [preOrder, setPreOrder] = useState<BuyCreditPreOrderResponse | null>(null);
  const [preOrderLoading, setPreOrderLoading] = useState(false);

  const [widgets, setWidgets] = useState<any>(null);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const paymentMethodWidgetRef = useRef<any>(null);
  const agreementWidgetRef = useRef<any>(null);

  // 중복 클릭 방지
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const paymentTimeoutRef = useRef<number | null>(null);

  // 결제 차단 모달 (약관 동의 미체크 등) — 토스트 대신 명확한 팝업으로
  const [blockModal, setBlockModal] = useState<{ title: string; message: string } | null>(null);

  // 자동 진입 가드 — URL packageSeq hint 가 있으면 preOrder 자동 호출 (한 번만)
  const autoStartedRef = useRef(false);
  // URL hint 진입 — preOrder 완료까지 패키지 선택 UI 숨기고 로딩만 표시
  const [autoLaunching, setAutoLaunching] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).has("packageSeq");
  });

  /** 패키지 → preOrder 호출 (수동/자동 공용). */
  const startPaymentWith = useCallback(async (pkg: CreditPackage) => {
    if (isPaymentProcessing) return;
    setIsPaymentProcessing(true);
    setPreOrderLoading(true);
    try {
      const resp = await walletChargeApi.preOrder(pkg.packageSeq);
      console.log("[CHARGE] preOrder 응답:", resp);
      if (!resp || typeof resp !== "object") {
        throw new Error("응답이 비어있어요");
      }
      if (resp.payAmount == null || resp.orderId == null) {
        console.error("[CHARGE] 응답 구조 비정상:", resp);
        throw new Error(
          "결제 정보가 누락되었습니다. (orderId/payAmount 누락) — 관리자에게 문의해주세요.",
        );
      }
      setPreOrder(resp);
      setIsPaymentProcessing(false);
    } catch (e) {
      add("결제 준비 실패: " + (e instanceof Error ? e.message : String(e)));
      setIsPaymentProcessing(false);
      // 자동 진행 실패 시 패키지 선택 화면으로 fallback
      setAutoLaunching(false);
    } finally {
      setPreOrderLoading(false);
    }
  }, [isPaymentProcessing, add]);

  // 첫 진입 시 packageSeq URL hint 우선 — 자동으로 preOrder 까지 진행
  useEffect(() => {
    if (packages.length === 0) return;
    if (selectedPkg) return;

    const params = new URLSearchParams(window.location.search);
    const hintRaw = params.get("packageSeq");
    const hintSeq = hintRaw ? parseInt(hintRaw, 10) : null;
    const hinted = hintSeq ? packages.find((p) => p.packageSeq === hintSeq) : null;

    if (hinted && !autoStartedRef.current) {
      // 크레딧 카드에서 진입 — 패키지 자동 선택 + preOrder 자동 시작 (한 단계 생략)
      autoStartedRef.current = true;
      setSelectedPkg(hinted);
      startPaymentWith(hinted);
      return;
    }

    // hint 없이 직접 진입한 경우 — 추천 패키지 선택만 (수동 [결제하기] 흐름)
    const recommended = packages.find((p) => (p.discountRate ?? 0) > 0) ?? packages[0];
    setSelectedPkg(recommended);
  }, [packages, selectedPkg, startPaymentWith]);

  // 사용자가 [패키지 변경] 으로 다른 패키지를 고를 때 위젯 초기화
  // (URL hint 자동 진입과 충돌 방지 — autoStartedRef 가 true 면 skip)
  const prevSeqRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const cur = selectedPkg?.packageSeq;
    if (prevSeqRef.current !== undefined && prevSeqRef.current !== cur) {
      setPreOrder(null);
      setWidgets(null);
      setIsWidgetReady(false);
      paymentMethodWidgetRef.current = null;
      agreementWidgetRef.current = null;
    }
    prevSeqRef.current = cur;
  }, [selectedPkg?.packageSeq]);

  /** [결제하기] 수동 클릭 — 패키지 목록에서 사용. */
  const handleStartPayment = useCallback(() => {
    if (selectedPkg) startPaymentWith(selectedPkg);
  }, [selectedPkg, startPaymentWith]);

  // 토스 v2 SDK 로드 — preOrder 받은 후
  useEffect(() => {
    if (!preOrder) return;

    const clientKey = preOrder.widgetClientKey;
    const customerKey = preOrder.customerKey;

    const initToss = () => {
      const TP = (window as any).TossPayments;
      if (!TP) return;
      const tp = TP(clientKey);
      const w = tp.widgets({ customerKey });
      setWidgets(w);
    };

    const SDK_URL = "https://js.tosspayments.com/v2/standard";
    const existing = document.querySelector(`script[src="${SDK_URL}"]`);
    if (existing) {
      if ((window as any).TossPayments) initToss();
      else existing.addEventListener("load", initToss, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = SDK_URL;
    script.onload = initToss;
    document.head.appendChild(script);
  }, [preOrder]);

  // 위젯 렌더링
  const renderWidget = useCallback(async () => {
    if (!widgets || !preOrder) return;
    try {
      // 기존 위젯 정리
      if (paymentMethodWidgetRef.current) {
        await paymentMethodWidgetRef.current.destroy();
        paymentMethodWidgetRef.current = null;
      }
      if (agreementWidgetRef.current) {
        await agreementWidgetRef.current.destroy();
        agreementWidgetRef.current = null;
      }

      await widgets.setAmount({ currency: "KRW", value: preOrder.payAmount });

      const [pm, ag] = await Promise.all([
        widgets.renderPaymentMethods({ selector: "#wallet-payment-widget", variantKey: "DEFAULT" }),
        widgets.renderAgreement({ selector: "#wallet-agreement-widget", variantKey: "AGREEMENT" }),
      ]);
      paymentMethodWidgetRef.current = pm;
      agreementWidgetRef.current = ag;

      // 브랜드페이 차단 (기존 정책 따름)
      pm.on("paymentMethodSelect", (sel: any) => {
        if (sel.method === "BRANDPAY" || sel.code === "BRANDPAY") {
          add("브랜드페이는 현재 사용할 수 없습니다. 다른 결제수단을 선택해주세요.");
          setIsWidgetReady(false);
          return;
        }
        setIsWidgetReady(true);
      });
      setIsWidgetReady(true);
    } catch (e) {
      console.error("위젯 렌더링 실패:", e);
      add("결제 위젯을 불러오는데 실패했습니다.");
    }
  }, [widgets, preOrder, add]);

  useEffect(() => {
    if (!widgets || !preOrder) return;
    if (paymentMethodWidgetRef.current) return;
    const t = setTimeout(renderWidget, 100);
    return () => clearTimeout(t);
  }, [widgets, preOrder?.orderId, renderWidget]);

  /** 결제 위젯에서 [결제하기] — 토스 결제창 띄움. */
  const handleRequestPayment = useCallback(async () => {
    if (!widgets || !preOrder || !isWidgetReady) {
      setBlockModal({
        title: "결제 준비 중",
        message: "결제 위젯이 아직 준비되지 않았어요. 잠시 후 다시 시도해 주세요.",
      });
      return;
    }
    setIsPaymentProcessing(true);

    // 30초 타임아웃 안전장치
    if (paymentTimeoutRef.current) clearTimeout(paymentTimeoutRef.current);
    paymentTimeoutRef.current = window.setTimeout(() => {
      setIsPaymentProcessing(false);
      paymentTimeoutRef.current = null;
    }, 30000);

    try {
      const baseUrl = (import.meta as any).env?.VITE_APP_URL || window.location.origin;
      const successUrl =
        `${baseUrl}/business/layout/user-credit-wallet-charge-success` +
        `?orderId=${encodeURIComponent(preOrder.orderId)}` +
        `&amount=${preOrder.payAmount}`;
      const failUrl =
        `${baseUrl}/business/layout/user-credit-wallet-charge-fail` +
        `?orderId=${encodeURIComponent(preOrder.orderId)}`;

      const params: any = {
        orderId: preOrder.orderId,
        orderName: preOrder.orderName,
        successUrl,
        failUrl,
        customerEmail: preOrder.customerEmail || undefined,
        customerName: preOrder.customerName || undefined,
      };

      await widgets.requestPayment(params);
      // 결제창 호출 성공 — 외부 앱으로 이동
      setIsPaymentProcessing(false);
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }
    } catch (e: any) {
      // 토스 SDK 가 던지는 에러 분류 — 약관 동의 미체크 / 사용자 취소는 정상 케이스
      const msg = (e?.message ?? "").toString();
      const code = (e?.code ?? "").toString();
      const isAgreement =
        code === "NEED_AGREEMENT" ||
        msg.includes("약관") ||
        msg.includes("동의");
      const isUserCancel =
        code === "USER_CANCEL" ||
        msg.includes("취소") ||
        msg.toLowerCase().includes("cancel");

      if (isAgreement) {
        // 예상된 검증 실패 — warn 로만 (콘솔 노이즈 감소)
        console.warn("[CHARGE] 약관 미동의:", msg);
        setBlockModal({
          title: "약관 동의가 필요해요",
          message: "결제를 진행하려면 아래 필수 약관에 동의해 주세요.",
        });
      } else if (isUserCancel) {
        // 사용자가 결제창에서 취소 — 모달도 띄우지 않고 조용히 복귀
        console.info("[CHARGE] 결제 취소됨:", msg);
      } else {
        console.error("[CHARGE] 결제 요청 실패:", e);
        setBlockModal({
          title: "결제 요청 실패",
          message: msg || "잠시 후 다시 시도해 주세요.",
        });
      }
      setIsPaymentProcessing(false);
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }
    }
  }, [widgets, preOrder, isWidgetReady]);

  /* ───── 렌더 ───── */

  // BE 응답 필드 누락에도 안전하도록 방어
  const fmtKrw = (n: number | undefined | null) =>
    "₩" + (Number(n ?? 0)).toLocaleString();
  const fmtCredit = (n: number | undefined | null) =>
    Number(n ?? 0).toLocaleString();

  return (
    <>
      <LayoutHeader
        title="크레딧 충전"
        hasBackBtn
        hasFootPrint={false}
        onBackClick={() => navigate({ to: "/business/layout/user-credit-wallet", replace: true })}
      />
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5] px-4 py-4">
        {/* URL hint 로 자동 진행 중 — 패키지 선택 단계 숨김, 로더만 표시 */}
        {autoLaunching && !preOrder && (
          <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 text-gray-500">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9934]" />
            <div className="text-sm">결제 준비 중…</div>
          </div>
        )}

        {/* 패키지 선택 */}
        {!preOrder && !autoLaunching && (
          <>
            {/* 보유 크레딧 — 기획 상단 표시 */}
            <div className="mb-4 rounded-xl bg-white border border-[#F0F0F0] p-4">
              <div className="text-[12px] text-[#999]">보유크레딧</div>
              <div className="mt-0.5">
                <span className="text-[22px] font-bold text-[#111]">
                  {fmtCredit(wallet?.balance)}
                </span>
                <span className="ml-1 text-[13px] text-[#888] font-medium">{CREDIT.unit}</span>
              </div>
            </div>

            <h2 className="mb-3 text-lg font-bold">충전 패키지 선택</h2>
            {pkgLoading && <div className="py-12 text-center text-sm text-gray-400">로딩 중…</div>}
            {!pkgLoading && packages.length === 0 && (
              <div className="rounded border border-gray-200 p-4 text-center text-sm text-gray-500">
                현재 노출 가능한 패키지가 없어요.
              </div>
            )}
            <div className="space-y-2">
              {packages.map((p) => {
                const selected = selectedPkg?.packageSeq === p.packageSeq;
                const bonusPct = p.discountRate ? Math.round(p.discountRate * 100) : 0;
                return (
                  <button
                    key={p.packageSeq}
                    type="button"
                    onClick={() => setSelectedPkg(p)}
                    className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
                      selected ? "border-[#FF9934] bg-[#FFF7E6]" : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-base font-bold">{p.packageName}</div>
                        <div className="mt-0.5 text-sm text-gray-600">
                          {CREDIT.emoji} {fmtCredit(p.amount)} 크레딧
                          {bonusPct > 0 && (
                            <span className="ml-1 text-emerald-600 font-semibold">
                              (+{bonusPct}% 보너스)
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-lg font-bold">{fmtKrw(p.priceKrw)}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* 요약 + 결제 시작 */}
            {selectedPkg && (
              <div className="mt-6 rounded-lg bg-gray-50 p-4">
                {/* 결제 금액 / 받을 크레딧 요약 — 기획상 미노출. 필요 시 주석 해제.
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">결제 금액</span>
                  <span className="font-bold">{fmtKrw(selectedPkg.priceKrw)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span className="text-gray-600">받을 크레딧</span>
                  <span className="font-bold text-emerald-700">
                    {CREDIT.emoji} {fmtCredit(selectedPkg.amount)}
                  </span>
                </div>
                */}
                <button
                  type="button"
                  onClick={handleStartPayment}
                  disabled={preOrderLoading || isPaymentProcessing}
                  className="w-full rounded-md bg-[#FF9934] py-3 text-base font-semibold text-white disabled:opacity-60"
                >
                  {preOrderLoading ? "준비 중..." : "결제하기"}
                </button>
              </div>
            )}
          </>
        )}

        {/* 토스 위젯 */}
        {preOrder && (
          <>
            <div className="mb-3 rounded-lg bg-gray-50 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">주문</span>
                <span className="font-medium">{preOrder.orderName}</span>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-gray-600">결제 금액</span>
                <span className="font-bold">{fmtKrw(preOrder.payAmount)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-gray-600">받을 크레딧</span>
                <span className="font-bold text-emerald-700">
                  {CREDIT.emoji} {fmtCredit(preOrder.creditAmount)}
                </span>
              </div>
            </div>

            <div id="wallet-payment-widget" />
            <div id="wallet-agreement-widget" className="mt-2" />

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setPreOrder(null);
                  setIsPaymentProcessing(false);
                  setAutoLaunching(false); // 사용자가 명시적으로 변경 — 패키지 목록 노출
                }}
                className="flex-1 rounded-md border border-gray-300 py-3 text-sm font-medium text-gray-700"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleRequestPayment}
                disabled={!isWidgetReady || isPaymentProcessing}
                className="flex-[2] rounded-md bg-[#FF9934] py-3 text-base font-semibold text-white disabled:opacity-60"
              >
                {isPaymentProcessing ? "처리 중..." : "결제 진행"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* 결제 차단/안내 모달 — 약관 미체크, 위젯 준비 안 됨 등 */}
      <MessageModal
        isOpen={blockModal !== null}
        onRequestClose={() => setBlockModal(null)}
        className="pt-6 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
        title={blockModal?.title ?? ""}
      >
        <div className="flex flex-col gap-4 items-center">
          <div className="text-center text-gray-700 px-2">{blockModal?.message}</div>
          <button
            onClick={() => setBlockModal(null)}
            className="py-3 w-full text-base font-semibold text-white bg-[#FF9934] rounded-lg"
          >
            확인
          </button>
        </div>
      </MessageModal>
    </>
  );
}
